import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { triggerCreditRefresh } from '../components/CreditDisplay';
import { useToast } from '../components/ui/Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function MockInterviewSetup() {
  const [searchParams] = useSearchParams();
  const preselectedAppId = searchParams.get('appId');

  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(preselectedAppId || '');
  const [duration, setDuration] = useState(30);
  const [loading, setLoading] = useState(false);
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { showError } = useToast();

  useEffect(() => {
    loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadApplications = async () => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/interview-prep/jobs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to load applications');

      const data = await response.json();
      setApplications(data.jobs || []);
    } catch (error) {
      showError('Failed to load job applications');
    }
  };

  const handleStart = async () => {
    if (!selectedApp) {
      showError('Please select a job application');
      return;
    }

    setLoading(true);

    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/mock-interview/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          application_id: selectedApp,
          duration_minutes: duration
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to start interview');
      }

      const result = await response.json();

      // Refresh credit display to show updated count
      triggerCreditRefresh();

      // Navigate to active interview
      navigate(`/mock-interview/${result.interview_id}`);
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getDurationInfo = (mins) => {
    const configs = {
      15: {
        label: 'Quick Practice',
        resume: '3 min',
        behavioral: '2 questions',
        technical: '2 questions'
      },
      30: {
        label: 'Standard Interview',
        resume: '5 min',
        behavioral: '3 questions',
        technical: '4 questions'
      },
      45: {
        label: 'In-Depth Practice',
        resume: '7 min',
        behavioral: '4 questions',
        technical: '5 questions'
      }
    };
    return configs[mins];
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-base)',
      color: 'var(--text-primary)',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '40px' }}>
          <button
            onClick={() => navigate('/interview-prep')}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-tertiary)',
              fontSize: '14px',
              cursor: 'pointer',
              marginBottom: '20px'
            }}
          >
            ← Back to Interview Prep
          </button>
          <h1 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '12px' }}>
            Mock Interview Setup
          </h1>
          <p style={{ color: 'var(--text-tertiary)', fontSize: '16px' }}>
            Practice a complete interview with FSM-based question flow and harsh, honest AI feedback.
          </p>
        </div>

        {/* Job Selector */}
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-light)',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>
            Select Job Application
          </h3>
          <select
            value={selectedApp}
            onChange={e => setSelectedApp(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '8px',
              color: 'var(--text-primary)',
              fontSize: '16px'
            }}
          >
            <option value="">-- Select a job --</option>
            {applications.map(app => (
              <option key={app.id} value={app.id}>
                {app.company_name} - {app.job_role}
              </option>
            ))}
          </select>
        </div>

        {/* Duration Selector */}
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-light)',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>
            Interview Length
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            {[15, 30, 45].map(mins => {
              const info = getDurationInfo(mins);
              return (
                <button
                  key={mins}
                  onClick={() => setDuration(mins)}
                  style={{
                    padding: '20px',
                    background: duration === mins
                      ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(79, 70, 229, 0.3) 100%)'
                      : 'var(--bg-surface)',
                    border: duration === mins
                      ? '2px solid var(--indigo-500)'
                      : '2px solid var(--border-light)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    textAlign: 'left'
                  }}
                >
                  <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
                    {mins} Minutes
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-tertiary)', marginBottom: '12px' }}>
                    {info.label}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    • Resume: {info.resume}<br />
                    • Behavioral: {info.behavioral}<br />
                    • Technical: {info.technical}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* What to Expect */}
        <div style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-light)',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '32px'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>
            What to Expect
          </h3>
          <ul style={{ marginLeft: '20px', color: 'var(--text-secondary)', lineHeight: '2' }}>
            <li>Resume walkthrough and follow-up questions</li>
            <li>Behavioral questions (STAR format recommended)</li>
            <li>Technical open-ended questions</li>
            <li>Technical MCQ questions (without options shown)</li>
            <li>Your questions for the interviewer</li>
            <li>Harsh, honest AI feedback at the end</li>
            <li>Hiring decision (hire/maybe/no_hire)</li>
          </ul>
        </div>

        {/* Start Button */}
        <button
          onClick={handleStart}
          disabled={loading || !selectedApp}
          style={{
            width: '100%',
            padding: '16px',
            background: (!selectedApp || loading)
              ? 'rgba(99, 102, 241, 0.3)'
              : 'var(--gradient-primary)',
            color: 'var(--text-primary)',
            border: 'none',
            borderRadius: '12px',
            cursor: (!selectedApp || loading) ? 'not-allowed' : 'pointer',
            fontSize: '18px',
            fontWeight: '700',
            transition: 'all 0.2s'
          }}
        >
          {loading ? 'Starting Interview...' : 'Start Interview →'}
        </button>
      </div>
    </div>
  );
}

export default MockInterviewSetup;
